import type { ComplaintCategory } from "./complaints.js";
import { contactPhone } from "./identity.js";

/**
 * What a contractor does, from a fixed list, so a committee looking for a
 * plumber finds every plumber and not only the ones somebody happened to
 * describe the same way.
 */
export const CONTRACTOR_TRADES = [
  "plumbing",
  "electrical",
  "lift",
  "carpentry",
  "painting",
  "masonry",
  "metalwork",
  "cleaning",
  "security",
  "pestControl",
  "gardening",
  "other",
] as const;
export type ContractorTrade = (typeof CONTRACTOR_TRADES)[number];

/**
 * Each trade in English, written into a listing's `service` as well, so a
 * phone that predates trades still shows a word rather than nothing.
 */
export const TRADE_NAMES: Record<ContractorTrade, string> = {
  plumbing: "Plumbing",
  electrical: "Electrical",
  lift: "Lift repair",
  carpentry: "Carpentry",
  painting: "Painting",
  masonry: "Building and tiling",
  metalwork: "Gates and welding",
  cleaning: "Cleaning",
  security: "Security",
  pestControl: "Pest control",
  gardening: "Gardening",
  other: "Other",
};

/** Words that place a listing written before trades existed. */
const TRADE_WORDS: [ContractorTrade, RegExp][] = [
  ["plumbing", /plumb|pipe|tap|water|ቧንቧ/i],
  ["electrical", /electr|wiring|light|ኤሌክትሪክ/i],
  ["lift", /lift|elevator|ሊፍት/i],
  ["carpentry", /carpent|wood|door|እንጨት/i],
  ["painting", /paint|ቀለም/i],
  ["masonry", /mason|tile|tiling|build|construct|ግንብ|ሰድር/i],
  ["metalwork", /metal|weld|gate|ብረት|ብየዳ/i],
  ["cleaning", /clean|ጽዳት/i],
  ["security", /secur|guard|ጥበቃ/i],
  ["pestControl", /pest|insect|ተባይ/i],
  ["gardening", /garden|ትክልት/i],
];

export function isTrade(value: unknown): value is ContractorTrade {
  return CONTRACTOR_TRADES.some((trade) => trade === value);
}

/** A listing's trade: the one it was saved with, or the one its words point to. */
export function tradeOf(listing: { trade?: unknown; service: string }): ContractorTrade {
  if (isTrade(listing.trade)) return listing.trade;
  return TRADE_WORDS.find(([, words]) => words.test(listing.service))?.[0] ?? "other";
}

/** The trade a repair most likely needs, or null when its category does not say. */
export function tradeForRepair(category: ComplaintCategory): ContractorTrade | null {
  return category === "other" ? null : category;
}

export interface AdvertisementInput {
  businessName: string;
  service: string;
  description: string;
  phone: string;
  trade?: ContractorTrade;
}

export function advertisementInput(input: AdvertisementInput): AdvertisementInput {
  const businessName = input.businessName.trim();
  const service = input.service.trim();
  const description = input.description.trim();
  if (
    !businessName ||
    businessName.length > 120 ||
    !service ||
    service.length > 80 ||
    !description ||
    description.length > 1500
  ) {
    throw new Error("Enter a name, service, and description within their length limits");
  }
  if (input.trade !== undefined && !isTrade(input.trade)) {
    throw new Error("Choose a trade from the list");
  }
  return {
    businessName,
    service,
    description,
    phone: contactPhone(input.phone),
    ...(input.trade === undefined ? {} : { trade: input.trade }),
  };
}
